import type { InputElementProps } from '@chakra-ui/react';
import * as React from 'react';
import {
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  Box,
  Field,
  Group,
  InputElement,
  CloseButton,
  Span,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { labelStyles } from './label';
import { InfoTip, Alert } from '.';
export interface InputProps {
  value?: string;
  defaultValue?: string;
  onChange?: (e: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  isClearable?: boolean;
  triggerResetToDefault?: number;
  label?: React.ReactNode;
  infoTip?: React.ReactNode;
  placeholder?: string;
  size?: ChakraInputProps['size'];
  startElementProps?: InputElementProps;
  endElementProps?: InputElementProps;
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  startOffset?: InputElementProps['paddingStart'];
  endOffset?: InputElementProps['paddingEnd'];
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  type?: ChakraInputProps['type'];
}

export const Input = React.forwardRef<HTMLDivElement, InputProps>(
  function Input(props, ref) {
    const {
      value,
      defaultValue = '',
      onChange,
      onBlur,
      disabled,
      isClearable = true,
      triggerResetToDefault = 0,
      label,
      infoTip,
      placeholder,
      size = 'md',
      startElement,
      startElementProps,
      startOffset = '6px',
      endElement,
      endElementProps,
      endOffset = '6px',
      width = '100%',
      minWidth = '300px',
      maxWidth = '400px',
      ...rest
    } = props;

    const [inputValue, setInputValue] = useState<string>(String(defaultValue));
    const inputRef = useRef<HTMLInputElement | null>(null);
    const triggerResetToDefaultRef = React.useRef(triggerResetToDefault);
    const clearElement =
      isClearable && inputValue && inputValue.length > 0 ? (
        <CloseButton
          aria-label="Clear input"
          variant="plain"
          focusVisibleRing="none"
          focusRingWidth="0px"
          color="white"
          size="xs"
          onClick={() => {
            setInputValue('');
            onChange?.('');
            inputRef.current?.focus();
          }}
        />
      ) : undefined;

    React.useEffect(() => {
      if (triggerResetToDefaultRef.current === triggerResetToDefault - 1) {
        // setInputValue(String(defaultValue ?? ''));
        onChange?.(defaultValue ?? '');
        triggerResetToDefaultRef.current = triggerResetToDefault;
      }
    }, [triggerResetToDefault]);

    React.useEffect(() => {
      if (value !== undefined && value !== inputValue) {
        setInputValue(String(value ?? ''));
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      onChange?.(e.target.value);
    };

    return (
      <Field.Root>
        <Box pos="relative" w="full">
          <Group ref={ref} display={'flex'}>
            {startElement && (
              <InputElement pointerEvents="none" {...startElementProps}>
                {startElement}
              </InputElement>
            )}
            <ChakraInput
              value={inputValue ?? ''}
              ref={inputRef}
              disabled={disabled}
              size={size}
              placeholder={placeholder}
              _placeholder={labelStyles.placeholder({})}
              onChange={handleChange}
              onBlur={onBlur}
              {...(startElement && {
                ps: startOffset,
              })}
              {...(((endElement || clearElement) && {
                pe: endOffset,
              }) as any)}
              width={width}
              minWidth={minWidth}
              maxWidth={maxWidth}
              {...rest}
            />
            {endElement || clearElement ? (
              <InputElement placement="end" {...endElementProps} {...rest}>
                {endElement}
                {isClearable && clearElement}
              </InputElement>
            ) : null}
          </Group>

          {label && (
            <Field.Label
              {...(labelStyles.floating({}) as any)}
              disabled={disabled}
            >
              {label}{' '}
              {infoTip && (
                <Span pointerEvents="auto">
                  <InfoTip>
                    <Alert>{infoTip}</Alert>
                  </InfoTip>
                </Span>
              )}
            </Field.Label>
          )}
        </Box>
      </Field.Root>
    );
  },
);
